
import React, { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useBateaux, Bateau } from '@/hooks/useBateaux';
import { useProAccess } from '@/hooks/useProAccess';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import FormulaireAjoutNavire from '@/components/bateaux/FormulaireAjoutNavire';
import FormulaireModificationBateau from '@/components/bateaux/FormulaireModificationBateau';
import BoatSensitiveInfo from '@/components/bateaux/BoatSensitiveInfo';
import { Anchor, Plus, Ship, Clock, Star, Crown, Eye } from 'lucide-react';
import HiddenDataBanner from '@/components/premium/HiddenDataBanner';
import MainLayout from '@/components/layout/MainLayout';
import 'flag-icons/css/flag-icons.min.css';
import { useTranslation } from 'react-i18next';

// Mapping des pavillons vers les codes ISO pour flag-icons
const getFlagCode = (pavillon: string | null | undefined): string | null => {
  if (!pavillon) return null;
  const codes: Record<string, string> = {
    "France": "fr",
    "Belgique": "be",
    "Suisse": "ch",
    "Monaco": "mc",
    "Italie": "it",
    "Espagne": "es",
    "Portugal": "pt",
    "Royaume-Uni": "gb",
    "Allemagne": "de",
    "Pays-Bas": "nl",
    "Grèce": "gr",
    "Croatie": "hr",
    "États-Unis": "us",
    "Canada": "ca",
    "Australie": "au",
    "Luxembourg": "lu",
    "Tunisie": "tn",
    "Maroc": "ma",
    "Turquie": "tr",
    "Pologne": "pl",
    "Danemark": "dk",
    "Norvège": "no",
    "Suède": "se",
    "Finlande": "fi",
  };
  return codes[pavillon] || null;
};

const MesBateauxPage = () => {
  const { t } = useTranslation('boats');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bateaux, loading, supprimerBateau, definirBateauParDefaut } = useBateaux();
  const { isProAccount } = useProAccess();
  const [showForm, setShowForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedBateau, setSelectedBateau] = useState<Bateau | null>(null);

  // Si un seul navire, afficher directement sa page détail
  useEffect(() => {
    if (!loading && bateaux.length === 1 && !showForm && !showEditForm) {
      navigate(`/navire/${bateaux[0].id}`);
    }
  }, [loading, bateaux, showForm, showEditForm, navigate]);

  const handleEditBateau = (bateau: Bateau) => {
    setSelectedBateau(bateau);
    setShowEditForm(true);
  };

  const handleViewBateau = (bateau: Bateau) => {
    navigate(`/navire/${bateau.id}`);
  };

  const closeEditForm = () => {
    setShowEditForm(false);
    setSelectedBateau(null);
  };

  const handleDefaultChange = async (bateauId: string, checked: boolean) => {
    if (checked) {
      await definirBateauParDefaut(bateauId);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Anchor className="h-12 w-12 text-nautic mx-auto mb-4 animate-pulse" />
            <p className="text-nautic">{t("mesBateaux.loading")}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-nautic">{t("mesBateaux.title")}</h1>
            <p className="text-gray-600 mt-2">
              {t("mesBateaux.subtitle")}
            </p>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-nautic hover:bg-nautic-light text-white flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {t("mesBateaux.addBoat")}
            {!isProAccount && <Crown className="h-4 w-4 text-nautic-gold ml-1" />}
          </Button>
        </div>

        {bateaux.length === 0 ? (
          <div className="text-center py-12">
            <Ship className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {t("mesBateaux.emptyTitle")}
            </h3>
            <p className="text-gray-500 mb-6">
              {t("mesBateaux.emptySubtitle")}
            </p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-nautic hover:bg-nautic-light text-white"
            >
              {t("mesBateaux.addFirstBoat")}
            </Button>
          </div>
        ) : (
           <div className="space-y-4">
            {!isProAccount && bateaux.length > 1 && (
              <HiddenDataBanner 
                hiddenCount={bateaux.length - 1}
                itemLabel={t("mesBateaux.hiddenDataItemLabel")}
              />
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(isProAccount ? bateaux : bateaux.slice(0, 1)).map((bateau) => (
              <Card key={bateau.id} className="hover:shadow-lg transition-shadow">
                {bateau.photo_url && (
                  <div className="relative h-48 w-full overflow-hidden rounded-t-lg">
                    <img 
                      src={bateau.photo_url} 
                      alt={bateau.nom} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg text-nautic flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      {bateau.nom}
                      {bateau.par_defaut && <Star className="h-4 w-4 text-yellow-500 fill-current" />}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewBateau(bateau)}
                      className="text-nautic hover:text-nautic-light hover:bg-nautic/5"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Case à cocher par défaut */}
                  <div className="flex items-center space-x-2 mb-4 p-2 bg-gray-50 rounded">
                    <Checkbox
                      id={`default-${bateau.id}`}
                      checked={bateau.par_defaut}
                      onCheckedChange={(checked) => handleDefaultChange(bateau.id, checked as boolean)}
                    />
                    <label
                      htmlFor={`default-${bateau.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {t("mesBateaux.defaultBoat")}
                    </label>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">{t("mesBateaux.constructeur")}</span>
                      <span>{bateau.nom_constructeur}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">{t("mesBateaux.modele")}</span>
                      <span>{bateau.nom_modele}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">{t("mesBateaux.annee")}</span>
                      <span>{bateau.annee}</span>
                    </div>
                    {bateau.annee_construction && (
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">{t("mesBateaux.construction")}</span>
                        <span>{bateau.annee_construction}</span>
                      </div>
                    )}
                    {bateau.annee_achat && (
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">{t("mesBateaux.achat")}</span>
                        <span>{bateau.annee_achat}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">{t("mesBateaux.dimensions")}</span>
                      <span>{bateau.longueur}m × {bateau.largeur}m</span>
                    </div>
                    {bateau.tirant_eau && (
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">{t("mesBateaux.tirantEau")}</span>
                        <span>{bateau.tirant_eau}m</span>
                      </div>
                    )}
                    {bateau.couleur && (
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">{t("mesBateaux.couleur")}</span>
                        <span>{bateau.couleur}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">{t("mesBateaux.moteur")}</span>
                      <span className="truncate ml-2">{bateau.moteur}</span>
                    </div>
                    {bateau.heures_moteur && (
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600 flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {t("mesBateaux.heuresMoteur")}
                        </span>
                        <span>{bateau.heures_moteur}h</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">{t("mesBateaux.carburant")}</span>
                      <span>{bateau.capacite_carburant}L</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">{t("mesBateaux.eau")}</span>
                      <span>{bateau.capacite_eau}L</span>
                    </div>
                    {bateau.capacite_eaux_usees && (
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">{t("mesBateaux.eauxUsees")}</span>
                        <span>{bateau.capacite_eaux_usees}L</span>
                      </div>
                    )}
                    <BoatSensitiveInfo
                      boatId={bateau.id}
                      immatriculation={bateau.immatriculation}
                      mmsi={bateau.mmsi}
                      numero_hin={bateau.numero_hin}
                    />
                    {bateau.port_attache && (
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">{t("mesBateaux.port")}</span>
                        <span>{bateau.port_attache}</span>
                      </div>
                    )}
                    {bateau.pavillon && (
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-600">{t("mesBateaux.pavillon")}</span>
                        <span className="flex items-center gap-2">
                          {getFlagCode(bateau.pavillon) && (
                            <span className={`fi fi-${getFlagCode(bateau.pavillon)} rounded`} style={{ fontSize: '1.2em' }}></span>
                          )}
                          {bateau.pavillon}
                        </span>
                      </div>
                    )}
                    {bateau.note && (
                      <div className="pt-2 border-t">
                        <span className="font-medium text-gray-600">{t("mesBateaux.note")}</span>
                        <p className="text-xs text-gray-500 mt-1">{bateau.note}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
           </div>
          </div>
        )}

        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("mesBateaux.addDialogTitle")}</DialogTitle>
            </DialogHeader>
            <FormulaireAjoutNavire onClose={() => setShowForm(false)} />
          </DialogContent>
        </Dialog>

        <Dialog open={showEditForm} onOpenChange={setShowEditForm}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("mesBateaux.editDialogTitle")}</DialogTitle>
            </DialogHeader>
            {selectedBateau && (
              <FormulaireModificationBateau 
                bateau={selectedBateau} 
                onClose={closeEditForm} 
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default MesBateauxPage;
